import React, { createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFetch } from '@stashinvest/use-fetch';
import { curry } from 'lodash';

export type EntitlementURI =
	| 'feature:invest:account:custodial:self'
	| 'feature:invest:account:custodial:robo'
	| 'feature:invest:account:retirement:self'
	| 'feature:invest:account:retirement:robo'
	| 'feature:invest:account:personal:robo'
	| 'feature:invest:account:personal:self'
	| 'feature:invest:stocks-and-etfs'
	| 'feature:invest:curated-stocks-and-etfs'
	| 'feature:content:report:standard'
	| 'feature:content:ai:money-coach'
	| 'feature:content:money-coach'
	| 'feature:content:diversification-score'
	| 'feature:solutions:insurance:avibra:31000offer'
	| 'feature:solutions:insurance:avibra:premium'
	| 'feature:content:content-moat'
	| 'feature:bank:card:premium'
	| 'feature:bank:stockback'
	| 'feature:bank:stockback:premium'
	| 'feature:bank:stockback:merchant-bonus:premium'
	| 'feature:bank:stockback:merchant-bonus:standard'
	| 'feature:bank:early-paycheck'
	| 'feature:bank:checking'
	| 'feature:content:report:premium'
	| 'feature:rewards:ira-deposit-match:standard';

export type EntitlementRecord = {
	uri: EntitlementURI;
};

export const OPEN_ACCOUNT_CUSTODIAL_SELF: EntitlementURI =
	'feature:invest:account:custodial:self';
export const OPEN_ACCOUNT_CUSTODIAL_ROBO: EntitlementURI =
	'feature:invest:account:custodial:robo';
export const OPEN_ACCOUNT_RETIREMENT_SELF: EntitlementURI =
	'feature:invest:account:retirement:self';
export const OPEN_ACCOUNT_RETIREMENT_ROBO: EntitlementURI =
	'feature:invest:account:retirement:robo';
export const OPEN_ACCOUNT_PERSONAL_SELF: EntitlementURI =
	'feature:invest:account:personal:self';
export const OPEN_ACCOUNT_PERSONAL_ROBO: EntitlementURI =
	'feature:invest:account:personal:robo';
export const INVEST_STOCKS_AND_ETFS: EntitlementURI = 'feature:invest:stocks-and-etfs';
export const INVEST_CURATED_STOCKS_AND_ETFS: EntitlementURI =
	'feature:invest:curated-stocks-and-etfs';
export const VIEW_CONTENT_REPORT_STANDARD: EntitlementURI =
	'feature:content:report:standard';
export const VIEW_CONTENT_AI_MONEY_COACH: EntitlementURI =
	'feature:content:ai:money-coach';
export const VIEW_CONTENT_MONEY_COACH: EntitlementURI = 'feature:content:money-coach';
export const VIEW_CONTENT_DIVERSIFICATION_SCORE: EntitlementURI =
	'feature:content:diversification-score';
export const ACCESS_SOLUTION_INSURANCE_AVIBRA_31000OFFER: EntitlementURI =
	'feature:solutions:insurance:avibra:31000offer';
export const ACCESS_SOLUTION_INSURANCE_AVIBRA_PREMIUM: EntitlementURI =
	'feature:solutions:insurance:avibra:premium';
export const VIEW_CONTENT_CORE: EntitlementURI = 'feature:content:content-moat';
export const BANK_CARD_PREMIUM: EntitlementURI = 'feature:bank:card:premium';
export const STOCK_BACK: EntitlementURI = 'feature:bank:stockback';
export const STOCK_BACK_PREMIUM: EntitlementURI = 'feature:bank:stockback:premium';
export const STOCK_BACK_MERCHANT_BONUS_PREMIUM: EntitlementURI =
	'feature:bank:stockback:merchant-bonus:premium';
export const STOCK_BACK_MERCHANT_BONUS_STANDARD: EntitlementURI =
	'feature:bank:stockback:merchant-bonus:standard';
export const BANK_EARLY_PAYCHECK: EntitlementURI = 'feature:bank:early-paycheck';
export const BANK_CHECKING: EntitlementURI = 'feature:bank:checking';
export const VIEW_CONTENT_REPORT_PREMIUM: EntitlementURI =
	'feature:content:report:premium';
export const IRA_DEPOSIT_MATCH_REWARDS: EntitlementURI =
	'feature:rewards:ira-deposit-match:standard';

const hasEntitlement = curry(
	(entitlements: EntitlementRecord[], entitlementUri: EntitlementURI) => {
		return entitlements.some((e) => e.uri === entitlementUri);
	}
);

// Plan-comparison endpoint (STASH2026-4625). Lets callers ask: "would upgrading
// to one of the eligible plans grant this entitlement?" without enumerating plans.
type PlanEntitlements = {
	id: string;
	name: string;
	uniqueName: string;
	ordering: number;
	entitlements: string[];
};

type PlanComparisonResponse = {
	currentPlan?: PlanEntitlements;
	eligiblePlans?: PlanEntitlements[];
};

const hasEligibleEntitlement = curry(
	(eligiblePlans: PlanEntitlements[], entitlementUri: EntitlementURI) => {
		return eligiblePlans.some((plan) => plan.entitlements.includes(entitlementUri));
	}
);

type EntitlementsContextType = {
	isHeldEntitlementsLoading: boolean;
	hasHeldEntitlementsError: boolean;
	isEntitlementsLoading: boolean;
	hasEntitlement: (entitlementUri: EntitlementURI) => boolean;
	hasEligibleEntitlement: (entitlementUri: EntitlementURI) => boolean;
	reloadEntitlements: () => void;
};

export const EntitlementsContext = createContext<EntitlementsContextType | null>(null);

type Props = {
	children: React.ReactNode;
};

// This provider allows us to cache the entitlements across the app
// There may be ways to configure the swr cache to do this more efficiently, but this is the simplest
// way without making significant changes to use-fetch.
export function EntitlementsProvider({ children }: Props) {
	const accessToken = useSelector(
		(state: { entities: { api_key?: { access_token?: string } } }) =>
			state.entities.api_key?.access_token
	);

	const { data, loading, error, fetch } = useFetch<{
		entitlements: EntitlementRecord[];
	}>({
		path: '/apis/customers/v1/subscriptions/entitlements',
		onMount: false,
		onError: (error: any) => {
			console.error('Error fetching entitlements', error);
		},
	});

	const {
		data: planComparisonData,
		loading: planComparisonLoading,
		error: planComparisonError,
		fetch: fetchPlanComparison,
	} = useFetch<PlanComparisonResponse>({
		path: '/apis/customers/v1/subscriptions/entitlements/plan-comparison',
		onMount: false,
		onError: (planError: any) => {
			console.error('Error fetching plan comparison', planError);
		},
	});

	useEffect(() => {
		if (accessToken) {
			fetch();
			fetchPlanComparison();
		}
	}, [accessToken, fetch, fetchPlanComparison]);

	const reloadEntitlements = () => {
		fetch();
		fetchPlanComparison();
	};

	const entitlements = data?.entitlements as EntitlementRecord[] | undefined;
	const eligiblePlans = planComparisonData?.eligiblePlans ?? [];

	const isHeldEntitlementsLoading = loading || (!data && !error);
	const hasHeldEntitlementsError = Boolean(error);

	// Loading is true until BOTH fetches have either returned data or surfaced an
	// error. Otherwise consumers gating on isEntitlementsLoading could call
	// hasEligibleEntitlement while planComparisonData is still undefined and
	// silently see "no eligible plans" for everything.
	const isEntitlementsLoading =
		loading ||
		planComparisonLoading ||
		(!data && !error) ||
		(!planComparisonData && !planComparisonError);

	return (
		<EntitlementsContext.Provider
			value={{
				isHeldEntitlementsLoading,
				hasHeldEntitlementsError,
				isEntitlementsLoading,
				// If the entitlements request errors, return false even when use-fetch retains stale data.
				hasEntitlement:
					!hasHeldEntitlementsError && entitlements
						? hasEntitlement(entitlements)
						: () => false,
				hasEligibleEntitlement: hasEligibleEntitlement(eligiblePlans),
				reloadEntitlements,
			}}
		>
			{children}
		</EntitlementsContext.Provider>
	);
}

export const useEntitlements = () => {
	const context = useContext(EntitlementsContext);

	if (!context) {
		throw new Error('useEntitlements must be used within an EntitlementsProvider');
	}

	return context;
};
